import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Image, Button, Tooltip} from 'antd';
import {FullscreenOutlined, FullscreenExitOutlined} from '@ant-design/icons';
import {Document, Page} from 'react-pdf';
import '../../../../utils/pdfWorker';
import {
  StyledInvoiceAttachmentPreview,
  StyledInvoiceAttachmentPreviewFile,
  StyledInvoiceAttachmentPreviewFullscreenButtonContainer,
} from './StyledInvoiceAttachmentPreview';
import {fileHelpers} from '../../../../utils/helpers';

const InvoiceAttachmentPreview = ({
  attachment,
  children,
  handleChangeFullscreen,
  onError,
  showFullscreenButton = true,
  ...rest
}) => {
  const [pages, setPages] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const isPdf = fileHelpers.hasExtensions(attachment?.name, ['.pdf']);

  const file = attachment?.file || undefined;

  const onLoadSuccess = ({numPages}) => {
    if (pages !== numPages) setPages(numPages);
    if (!isLoaded) setIsLoaded(true);
  }

  const onLoadError = () => {
    if (isLoaded) setIsLoaded(false);
    onError?.();
  }

  // only enable the fullscreen button when the prop allows it and the file is ready
  const isEnableFullscreenButton = showFullscreenButton && (isPdf ? isLoaded : Boolean(file));

  useEffect(() => {
    const onFullChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      setIsFullscreen(Boolean(fsEl));
      handleChangeFullscreen?.(Boolean(fsEl));
    };

    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
    ];

    events.forEach((ev) => document.addEventListener(ev, onFullChange));
    return () => events.forEach((ev) => document.removeEventListener(ev, onFullChange));
  }, [handleChangeFullscreen]);

  const openFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  const closeFullscreen = async () => {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    else if (document.msExitFullscreen) document.msExitFullscreen();
  };

  const toggleFullscreen = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFullscreen) closeFullscreen();
    else openFullscreen();
  };

  const previewButtonTitle = isFullscreen ? 'Exit fullscreen' : 'Open fullscreen';

  return (
    <StyledInvoiceAttachmentPreview ref={containerRef}>
      {isEnableFullscreenButton && (
        <StyledInvoiceAttachmentPreviewFullscreenButtonContainer fullscreen={String(isFullscreen)}>
          <Tooltip title={previewButtonTitle}>
            <Button
              size='small'
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              aria-label={previewButtonTitle}
            />
          </Tooltip>
        </StyledInvoiceAttachmentPreviewFullscreenButtonContainer>
      )}

      <StyledInvoiceAttachmentPreviewFile {...rest} fullscreen={String(isFullscreen)}>
        {isPdf ? (
          <Document
            file={file}
            onLoadSuccess={onLoadSuccess}
            onLoadError={onLoadError}
          >
            {[...Array(pages)].map((_, index) => (
              <Page
                key={index}
                pageNumber={index + 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            ))}
          </Document>
        ) : (
          <Image
            preview={false}
            src={file}
            onError={onError}
          />
        )}

      </StyledInvoiceAttachmentPreviewFile>
    </StyledInvoiceAttachmentPreview>
  );
}

InvoiceAttachmentPreview.propTypes = {
  attachment: PropTypes.shape({
    file: PropTypes.string,
    name: PropTypes.string
  }),
  showFullscreenButton: PropTypes.bool,
  handleChangeFullscreen: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.node,
}

export default InvoiceAttachmentPreview;
